import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { SearchFilterBar } from '../../components/SearchFilterBar';
import LmsDeleteConfirmModal from '../../components/LmsDeleteConfirmModal';
import LmsSessionAttendanceModal from '../../components/LmsSessionAttendanceModal';
import LmsSessionFeedbackModal from '../../components/LmsSessionFeedbackModal';
import LmsDeleteMenuItem from '../../components/LmsDeleteMenuItem';
import FloatingMenu from '../../components/FloatingMenu';
import { ContentLoadingState } from '../../components/ContentLoadingState';
import FilterDropdownButton from '../../components/FilterDropdownButton';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, ChevronDown, Edit2, Calendar, Clock, MapPin, Users, Video, Building, Trophy, CheckCircle, XCircle, AlertCircle, MessageSquare, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { lmsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

type SessionType = 'classroom' | 'video_conference';
// 'ended' is admin-only and read-derived: the session's slot has passed but
// nobody has worked the register, so it is over without being completed. It is
// never a value updateSessionStatus accepts — the admin resolves an ended
// session by marking attendance, or by forcing it Completed.
type SessionStatus = 'scheduled' | 'in_progress' | 'ended' | 'completed' | 'cancelled';
type SessionStatusOverride = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

interface TrainingSession {
  id: string;
  title: string;
  description: string | null;
  session_type: SessionType;
  status: SessionStatus;
  scheduled_at: string;
  duration_minutes: number;
  location: string | null;
  venue_name: string | null;
  trainer_name: string | null;
  audience: 'everyone' | 'assigned';
  assigned_count: number;
  max_attendees: number | null;
  points_reward: number;
  has_assessment: boolean;
  registered_count: number;
  attended_count: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

const sessionTypeConfig = {
  classroom: { label: 'Classroom', icon: Building, color: 'text-blue-600' },
  video_conference: { label: 'Video Conference', icon: Video, color: 'text-purple-600' },
};

const statusConfig = {
  scheduled: { label: 'Scheduled', bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: Clock },
  in_progress: { label: 'In Progress', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: AlertCircle },
  ended: { label: 'Ended', bgColor: 'bg-gray-100', textColor: 'text-gray-700', icon: Clock },
  completed: { label: 'Completed', bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', bgColor: 'bg-red-100', textColor: 'text-red-700', icon: XCircle },
};

export default function LmsSessionsPage() {
  const navigate = useNavigate();
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SessionStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<SessionType | 'all'>('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  // Each row's three-dots button, so its menu can be placed against the window.
  const menuAnchors = useRef<Record<string, HTMLDivElement | null>>({});
  const closeActiveDropdown = useCallback(() => setActiveDropdown(null), []);
  const [attendanceSession, setAttendanceSession] = useState<TrainingSession | null>(null);
  const [feedbackSession, setFeedbackSession] = useState<TrainingSession | null>(null);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await lmsAPI.getSessions({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        session_type: typeFilter !== 'all'
          ? (typeFilter === 'video_conference' ? 'virtual' : 'classroom')
          : undefined,
        projectId,
      });
      const data = response.data?.data || [];
      // Backend stores session_type as 'classroom' | 'virtual'; the list UI
      // uses 'classroom' | 'video_conference'. Normalize on read.
      setSessions(data.map((s: any) => ({
        id: s.id,
        title: s.title,
        description: s.description ?? null,
        session_type: s.session_type === 'virtual' ? 'video_conference' : 'classroom',
        status: s.status,
        scheduled_at: s.scheduled_at,
        duration_minutes: s.duration_minutes,
        location: s.session_type === 'virtual' ? (s.meeting_link ?? null) : (s.location ?? null),
        venue_name: s.venue_name ?? null,
        trainer_name: s.trainer_name ?? null,
        audience: s.audience === 'assigned' ? 'assigned' : 'everyone',
        assigned_count: parseInt(s.assigned_count) || 0,
        max_attendees: s.max_participants ?? null,
        points_reward: s.points_reward ?? 0,
        has_assessment: false,
        registered_count: parseInt(s.registered_count) || 0,
        attended_count: parseInt(s.attended_count) || 0,
        created_by: s.created_by ?? null,
        created_by_name: s.created_by_name ?? null,
        created_at: s.created_at,
      })));
      setError(null);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Failed to load training sessions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, projectId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Filter sessions
  const filteredSessions = sessions.filter((session) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return session.title.toLowerCase().includes(query) || session.description?.toLowerCase().includes(query);
    }
    return true;
  });

  // Handle delete
  const handleDelete = async (sessionId: string) => {
    try {
      setActionLoading(true);
      await lmsAPI.deleteSession(sessionId, projectId);
      setShowDeleteConfirm(null);
      fetchSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Failed to delete session');
    } finally {
      setActionLoading(false);
    }
  };

  // Force a lifecycle status. Passing 'scheduled' clears the override and
  // hands the session back to the time-derived status.
  const handleStatusChange = async (sessionId: string, status: SessionStatusOverride) => {
    try {
      setActionLoading(true);
      await lmsAPI.updateSessionStatus(sessionId, status, projectId);
      await fetchSessions();
    } catch (err) {
      console.error('Error updating session status:', err);
      alert('Failed to update session status');
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle cancel session
  const handleCancelSession = async (sessionId: string) => {
    try {
      setActionLoading(true);
      await lmsAPI.cancelSession(sessionId, projectId);
      fetchSessions();
    } catch (err) {
      console.error('Error cancelling session:', err);
      alert('Failed to cancel session');
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Training Sessions</h1>
          <p className="text-gray-500 mt-1">Schedule and manage live training sessions</p>
        </div>
        <button
          onClick={() => navigate('/lms/sessions/new')}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
        >
          <Plus className="w-4 h-4 inline-block mr-2" />
          Create Session
        </button>
      </div>

      {/* Content Card */}
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        {/* Filters */}
        <SearchFilterBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search sessions..."
        >
            {/* Status filter */}
            <div className="relative">
              <FilterDropdownButton
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                label={statusFilter === 'all' ? 'All Status' : statusConfig[statusFilter].label}
              />

              {showStatusDropdown && (
                <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {['all', 'scheduled', 'in_progress', 'ended', 'completed', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status as SessionStatus | 'all');
                        setShowStatusDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      {status === 'all' ? 'All Status' : statusConfig[status as SessionStatus].label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Type filter */}
            <div className="relative">
              <button
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-gray-700">
                  {typeFilter === 'all' ? 'All Types' : sessionTypeConfig[typeFilter].label}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {showTypeDropdown && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {['all', 'classroom', 'video_conference'].map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setTypeFilter(type as SessionType | 'all');
                        setShowTypeDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      {type === 'all' ? 'All Types' : sessionTypeConfig[type as SessionType].label}
                    </button>
                  ))}
                </div>
              )}
            </div>
        </SearchFilterBar>

        {/* Content */}
        {loading ? (
          <ContentLoadingState message="Loading training sessions..." />
        ) : error ? (
          <div className="text-center py-12">
            <Calendar className="w-8 h-8 mx-auto mb-4 text-red-500" />
            <p className="text-gray-500">{error}</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No training sessions yet</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters or search terms.'
                : 'Schedule your first training session to get started.'}
            </p>
            {!searchQuery && statusFilter === 'all' && typeFilter === 'all' && (
              <button
                onClick={() => navigate('/lms/sessions/new')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Schedule your first session
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/70 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Session</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Schedule</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Attendance</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Points</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSessions.map((session) => {
                    const typeConfig = sessionTypeConfig[session.session_type];
                    const statConfig = statusConfig[session.status] ?? statusConfig.scheduled;
                    const StatusIcon = statConfig.icon;
                    const TypeIcon = typeConfig.icon;
                    const { date, time } = formatDateTime(session.scheduled_at);

                    return (
                      <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{session.title}</p>
                            {session.venue_name && (
                              <div className="flex items-center gap-1">
                                <Building className="w-3.5 h-3.5 text-gray-400" />
                                <span className="truncate max-w-xs">{session.venue_name}</span>
                              </div>
                            )}
                            {session.audience === 'assigned' && (
                              <div className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5 text-amber-500" />
                                <span className="text-amber-700">
                                  {session.assigned_count} assigned only
                                </span>
                              </div>
                            )}
                            {session.trainer_name && (
                              <div className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5 text-gray-400" />
                                <span className="truncate max-w-xs">{session.trainer_name}</span>
                              </div>
                            )}
                            {session.location && (
                              <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                <span className="truncate max-w-xs">{session.location}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`flex items-center gap-1.5 text-sm ${typeConfig.color}`}>
                            <TypeIcon className="w-4 h-4" />
                            <span>{typeConfig.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statConfig.bgColor} ${statConfig.textColor}`}>
                            <StatusIcon className="w-3.5 h-3.5" />
                            {statConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="flex items-center gap-1 text-gray-900">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" />
                              {date}
                            </div>
                            <div className="flex items-center gap-1 text-gray-500 text-xs mt-0.5">
                              <Clock className="w-3 h-3" />
                              {time} · {formatDuration(session.duration_minutes)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {/* Opens the learners page: who was assigned, who registered, who attended. */}
                          <button
                            type="button"
                            onClick={() => navigate(`/lms/sessions/${session.id}/learners`)}
                            title="Attended / registered. Click to see each learner."
                            className="flex items-center gap-1 text-sm text-primary-600 hover:underline"
                          >
                            <Users className="w-4 h-4 text-gray-400" />
                            <span>{session.attended_count}/{session.registered_count}</span>
                            {session.max_attendees && (
                              <span className="text-gray-400">({session.max_attendees} max)</span>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Trophy className="w-4 h-4 text-yellow-500" />
                            <span>{session.points_reward} pts</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <TableActionGroup><div className="relative" ref={(el) => { menuAnchors.current[session.id] = el; }}>
                            <TableActionButton action="more" label="Session actions" aria-haspopup="menu" aria-expanded={activeDropdown === session.id}
                              onClick={() => setActiveDropdown(activeDropdown === session.id ? null : session.id)} />

                            {/* Drawn over the page, not inside the table: with one or
                                two rows the table is shorter than this menu and cut
                                most of it off. */}
                            {activeDropdown === session.id && (
                              <FloatingMenu role="menu" anchor={menuAnchors.current[session.id] ?? null} onClose={closeActiveDropdown} className="w-44">
                                <button
                                  onClick={() => {
                                    navigate(`/lms/sessions/${session.id}`);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/lms/sessions/${session.id}/assignments`);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                  <Users className="w-4 h-4" />
                                  Assign Learners
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/lms/sessions/${session.id}/learners`);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                  <ClipboardList className="w-4 h-4" />
                                  View Learners
                                </button>
                                {session.status !== 'cancelled' && (
                                  <button
                                    onClick={() => {
                                      setAttendanceSession(session);
                                      setActiveDropdown(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Attendance
                                  </button>
                                )}
                                {/* Learners are asked to rate a session after
                                    check-in. Until now nothing in the portal
                                    read those answers back. */}
                                {(session.status === 'completed' ||
                                  session.status === 'ended' ||
                                  session.status === 'in_progress') && (
                                    <button
                                      onClick={() => {
                                        setFeedbackSession(session);
                                        setActiveDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                    >
                                      <MessageSquare className="w-4 h-4" />
                                      Feedback
                                    </button>
                                  )}
                                {/* Status is normally derived from the clock.
                                    These are the overrides for a session that
                                    finished early or never ran as scheduled. */}
                                {session.status !== 'completed' && session.status !== 'cancelled' && (
                                  <button
                                    onClick={() => handleStatusChange(session.id, 'completed')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    Mark Completed
                                  </button>
                                )}
                                {(session.status === 'completed' || session.status === 'cancelled') && (
                                  <button
                                    onClick={() => handleStatusChange(session.id, 'scheduled')}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                                  >
                                    <Clock className="w-4 h-4" />
                                    Reopen
                                  </button>
                                )}
                                {session.status === 'scheduled' && (
                                  <>
                                    <div className="border-t border-gray-100" />
                                    <button
                                      onClick={() => handleCancelSession(session.id)}
                                      className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 transition-colors flex items-center gap-2"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Cancel
                                    </button>
                                  </>
                                )}
                                <div className="border-t border-gray-100" />
                                <LmsDeleteMenuItem
                                  onClick={() => {
                                    setShowDeleteConfirm(session.id);
                                    setActiveDropdown(null);
                                  }}
                                />
                              </FloatingMenu>
                            )}
                          </div>
                          </TableActionGroup></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Attendance Modal */}
      {attendanceSession && (
        <LmsSessionAttendanceModal
          sessionId={attendanceSession.id}
          sessionTitle={attendanceSession.title}
          projectId={projectId}
          onClose={() => setAttendanceSession(null)}
          onSaved={fetchSessions}
        />
      )}

      {/* Attendee Feedback Modal */}
      {feedbackSession && (
        <LmsSessionFeedbackModal
          sessionId={feedbackSession.id}
          sessionTitle={feedbackSession.title}
          projectId={projectId}
          onClose={() => setFeedbackSession(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <LmsDeleteConfirmModal
          title="Delete Session"
          message="Are you sure you want to delete this training session? This will also delete all attendance records. This action cannot be undone."
          onCancel={() => setShowDeleteConfirm(null)}
          onConfirm={() => handleDelete(showDeleteConfirm)}
          loading={actionLoading}
        />
      )}

      {/* Click outside to close dropdowns */}
      {(showStatusDropdown || showTypeDropdown) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowStatusDropdown(false);
            setShowTypeDropdown(false);
            setActiveDropdown(null);
          }}
        />
      )}
    </div>
  );
}
